'use client';

import React, { useState } from 'react';
import { Home, Plus, Search, Filter, Eye, Edit2, MoreVertical, Layers, MapPin, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
  MOCK_HOUSES, PoultryHouse, HouseStatus, HousingSystem,
  HOUSING_SYSTEM_LABELS, HOUSING_SYSTEM_COLORS,
  HOUSE_STATUS_LABELS, HOUSE_STATUS_COLORS,
} from './housesData';
import HousesSummaryCards from './HousesSummaryCards';

// ─── Status Badge ────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: HouseStatus }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${HOUSE_STATUS_COLORS[status]}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {HOUSE_STATUS_LABELS[status]}
    </span>
  );
}

// ─── System Badge ─────────────────────────────────────────────────────────────
function SystemBadge({ system }: { system: HousingSystem }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${HOUSING_SYSTEM_COLORS[system]}`}>
      {HOUSING_SYSTEM_LABELS[system]}
    </span>
  );
}

// ─── Occupancy Bar ────────────────────────────────────────────────────────────
function OccupancyBar({ current, capacity }: { current: number; capacity: number }) {
  const pct = capacity > 0 ? Math.min(100, Math.round((current / capacity) * 100)) : 0;
  const color = pct >= 95 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : pct > 0 ? 'bg-green-500' : 'bg-gray-200';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden min-w-16">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-600 tabular-nums w-8 text-right">{pct}%</span>
    </div>
  );
}

// ─── House Detail Modal ───────────────────────────────────────────────────────
function HouseDetailModal({ house, onClose }: { house: PoultryHouse; onClose: () => void }) {
  const occupancyPct = house.capacity > 0 ? Math.round((house.currentOccupancy / house.capacity) * 100) : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">{house.houseCode}</span>
              <StatusBadge status={house.status} />
              <SystemBadge system={house.housingSystem} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{house.houseName}</h2>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
              <MapPin size={12} />
              {house.farmName}{house.branchName ? ` › ${house.branchName}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Occupancy */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Capacity & Occupancy</p>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <p className="text-xs text-gray-400">Capacity</p>
                <p className="text-lg font-bold text-gray-900 font-tabular">{house.capacity.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Current Birds</p>
                <p className="text-lg font-bold text-gray-900 font-tabular">{house.currentOccupancy.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Occupancy</p>
                <p className={`text-lg font-bold font-tabular ${occupancyPct >= 95 ? 'text-red-600' : occupancyPct >= 75 ? 'text-amber-600' : 'text-green-600'}`}>{occupancyPct}%</p>
              </div>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${occupancyPct >= 95 ? 'bg-red-500' : occupancyPct >= 75 ? 'bg-amber-500' : occupancyPct > 0 ? 'bg-green-500' : 'bg-gray-300'}`}
                style={{ width: `${occupancyPct}%` }}
              />
            </div>
          </div>

          {/* Flock Link */}
          {house.flockBatch && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
              <Layers size={16} className="text-blue-600 flex-shrink-0" />
              <div>
                <p className="text-xs text-blue-500 font-medium">Active Flock</p>
                <p className="text-sm font-bold text-blue-800">{house.flockBatch} — {house.flockType}</p>
              </div>
            </div>
          )}

          {/* Dimensions & Systems */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">House Details</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Dimensions', value: `${house.length}m × ${house.width}m (${house.length * house.width} m²)` },
                { label: 'Manager', value: house.manager },
                { label: 'Ventilation', value: house.ventilationType },
                { label: 'Water System', value: house.waterSystem },
                { label: 'Feeding System', value: house.feedingSystem },
                { label: 'Opened', value: house.createdAt },
              ].map(item => (
                <div key={item.label} className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-400">{item.label}</p>
                  <p className="text-sm font-medium text-gray-800">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Biosecurity Dates */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Biosecurity & Maintenance</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Last Cleaned', value: house.lastCleanedDate || '—', warn: !house.lastCleanedDate },
                { label: 'Last Disinfected', value: house.lastDisinfectedDate || '—', warn: !house.lastDisinfectedDate },
                { label: 'Next Maintenance', value: house.nextMaintenanceDate || 'Not scheduled', warn: false },
              ].map(item => (
                <div key={item.label} className={`rounded-lg px-3 py-2 ${item.warn ? 'bg-red-50 border border-red-100' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${item.warn ? 'text-red-400' : 'text-gray-400'}`}>{item.label}</p>
                  <p className={`text-sm font-medium ${item.warn ? 'text-red-700' : 'text-gray-800'}`}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {house.notes && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-amber-600 mb-1">Notes</p>
              <p className="text-sm text-amber-800">{house.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Add / Edit House Modal ───────────────────────────────────────────────────
interface HouseFormData {
  houseCode: string;
  houseName: string;
  farmName: string;
  branchName: string;
  housingSystem: HousingSystem;
  status: HouseStatus;
  capacity: string;
  length: string;
  width: string;
  ventilationType: string;
  waterSystem: string;
  feedingSystem: string;
  manager: string;
  notes: string;
}

const EMPTY_FORM: HouseFormData = {
  houseCode: '', houseName: '', farmName: '', branchName: '',
  housingSystem: 'deep_litter', status: 'available',
  capacity: '', length: '', width: '',
  ventilationType: '', waterSystem: '', feedingSystem: '',
  manager: '', notes: '',
};

function HouseFormModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: Partial<HouseFormData>;
  onSave: (data: HouseFormData) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<HouseFormData>({ ...EMPTY_FORM, ...initial });
  const [errors, setErrors] = useState<Partial<Record<keyof HouseFormData, string>>>({});

  function set(field: keyof HouseFormData, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }

  function validate() {
    const e: Partial<Record<keyof HouseFormData, string>> = {};
    if (!form.houseCode.trim()) e.houseCode = 'Required';
    if (!form.houseName.trim()) e.houseName = 'Required';
    if (!form.farmName.trim()) e.farmName = 'Required';
    if (!form.capacity || isNaN(Number(form.capacity)) || Number(form.capacity) <= 0) e.capacity = 'Enter a valid capacity';
    return e;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave(form);
  }

  const Field = ({ label, field, type = 'text', placeholder = '' }: { label: string; field: keyof HouseFormData; type?: string; placeholder?: string }) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={form[field] as string}
        onChange={e => set(field, e.target.value)}
        placeholder={placeholder}
        className={`input-field text-sm ${errors[field] ? 'border-red-400' : ''}`}
      />
      {errors[field] && <p className="text-xs text-red-500 mt-0.5">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">{initial?.houseCode ? 'Edit House' : 'Add Poultry House'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Identity */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">House Identity</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="House Code *" field="houseCode" placeholder="e.g. IDF-H06" />
              <Field label="House Name *" field="houseName" placeholder="e.g. House 6 — Broiler C" />
              <Field label="Farm Name *" field="farmName" placeholder="e.g. Idudi Poultry Farm" />
              <Field label="Branch / Location" field="branchName" placeholder="e.g. Idudi Main Site" />
            </div>
          </div>

          {/* System & Status */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Housing System & Status</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Housing System</label>
                <select className="select-field text-sm" value={form.housingSystem} onChange={e => set('housingSystem', e.target.value as HousingSystem)}>
                  <option value="deep_litter">Deep Litter</option>
                  <option value="battery_cage">Battery Cage</option>
                  <option value="free_range">Free Range</option>
                  <option value="brooder">Brooder</option>
                  <option value="breeder_house">Breeder House</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                <select className="select-field text-sm" value={form.status} onChange={e => set('status', e.target.value as HouseStatus)}>
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="disinfection">Disinfection</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Capacity & Dimensions */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Capacity & Dimensions</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Capacity (birds) *" field="capacity" type="number" placeholder="e.g. 5000" />
              <Field label="Length (m)" field="length" type="number" placeholder="e.g. 60" />
              <Field label="Width (m)" field="width" type="number" placeholder="e.g. 12" />
            </div>
          </div>

          {/* Systems */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Infrastructure</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ventilation Type" field="ventilationType" placeholder="e.g. Natural cross-ventilation" />
              <Field label="Water System" field="waterSystem" placeholder="e.g. Nipple drinkers" />
              <Field label="Feeding System" field="feedingSystem" placeholder="e.g. Chain feeder" />
              <Field label="Manager" field="manager" placeholder="e.g. Sarah Namukasa" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Any additional notes about this house…"
              className="input-field text-sm resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">
              <Plus size={14} />
              {initial?.houseCode ? 'Save Changes' : 'Add House'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Content ─────────────────────────────────────────────────────────────
export default function HousesContent() {
  const [houses, setHouses] = useState<PoultryHouse[]>(MOCK_HOUSES);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');
  const [farmFilter, setFarmFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailHouse, setDetailHouse] = useState<PoultryHouse | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editHouse, setEditHouse] = useState<PoultryHouse | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  const uniqueFarms = Array.from(new Set(houses.map(h => h.farmName)));

  const filtered = houses.filter(h => {
    const q = search.toLowerCase();
    const matchSearch = !search || h.houseName.toLowerCase().includes(q) || h.houseCode.toLowerCase().includes(q) || h.farmName.toLowerCase().includes(q) || (h.branchName || '').toLowerCase().includes(q) || (h.flockBatch || '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || h.status === statusFilter;
    const matchSystem = systemFilter === 'all' || h.housingSystem === systemFilter;
    const matchFarm = farmFilter === 'all' || h.farmName === farmFilter;
    return matchSearch && matchStatus && matchSystem && matchFarm;
  });

  function handleSave(data: ReturnType<typeof EMPTY_FORM extends infer T ? () => T : never>) {
    // handled below via explicit type
  }

  function handleAddSave(data: { houseCode: string; houseName: string; farmName: string; branchName: string; housingSystem: HousingSystem; status: HouseStatus; capacity: string; length: string; width: string; ventilationType: string; waterSystem: string; feedingSystem: string; manager: string; notes: string }) {
    const newHouse: PoultryHouse = {
      id: `house-${Date.now()}`,
      houseCode: data.houseCode,
      houseName: data.houseName,
      farmId: `farm-${Date.now()}`,
      farmName: data.farmName,
      branchId: data.branchName ? `branch-${Date.now()}` : null,
      branchName: data.branchName || null,
      housingSystem: data.housingSystem,
      status: data.status,
      capacity: Number(data.capacity) || 0,
      currentOccupancy: 0,
      flockId: null,
      flockBatch: null,
      flockType: null,
      length: Number(data.length) || 0,
      width: Number(data.width) || 0,
      ventilationType: data.ventilationType,
      waterSystem: data.waterSystem,
      feedingSystem: data.feedingSystem,
      lastCleanedDate: null,
      lastDisinfectedDate: null,
      nextMaintenanceDate: null,
      manager: data.manager,
      notes: data.notes,
      createdAt: new Date().toISOString().split('T')[0],
    };
    setHouses(prev => [newHouse, ...prev]);
    setShowAddForm(false);
    showToast(`House "${newHouse.houseName}" added successfully.`);
  }

  function handleEditSave(data: { houseCode: string; houseName: string; farmName: string; branchName: string; housingSystem: HousingSystem; status: HouseStatus; capacity: string; length: string; width: string; ventilationType: string; waterSystem: string; feedingSystem: string; manager: string; notes: string }) {
    if (!editHouse) return;
    setHouses(prev => prev.map(h => h.id === editHouse.id ? {
      ...h,
      houseCode: data.houseCode,
      houseName: data.houseName,
      farmName: data.farmName,
      branchName: data.branchName || null,
      housingSystem: data.housingSystem,
      status: data.status,
      capacity: Number(data.capacity) || h.capacity,
      length: Number(data.length) || h.length,
      width: Number(data.width) || h.width,
      ventilationType: data.ventilationType,
      waterSystem: data.waterSystem,
      feedingSystem: data.feedingSystem,
      manager: data.manager,
      notes: data.notes,
    } : h));
    setEditHouse(null);
    showToast(`House "${data.houseName}" updated.`);
  }

  function handleStatusChange(houseId: string, newStatus: HouseStatus) {
    setHouses(prev => prev.map(h => h.id === houseId ? { ...h, status: newStatus } : h));
    setOpenMenuId(null);
    showToast('House status updated.');
  }

  return (
    <div className="p-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Modals */}
      {detailHouse && <HouseDetailModal house={detailHouse} onClose={() => setDetailHouse(null)} />}
      {showAddForm && <HouseFormModal onSave={handleAddSave} onClose={() => setShowAddForm(false)} />}
      {editHouse && (
        <HouseFormModal
          initial={{
            houseCode: editHouse.houseCode,
            houseName: editHouse.houseName,
            farmName: editHouse.farmName,
            branchName: editHouse.branchName || '',
            housingSystem: editHouse.housingSystem,
            status: editHouse.status,
            capacity: String(editHouse.capacity),
            length: String(editHouse.length),
            width: String(editHouse.width),
            ventilationType: editHouse.ventilationType,
            waterSystem: editHouse.waterSystem,
            feedingSystem: editHouse.feedingSystem,
            manager: editHouse.manager,
            notes: editHouse.notes,
          }}
          onSave={handleEditSave}
          onClose={() => setEditHouse(null)}
        />
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Poultry Houses</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage housing units across farms — capacity constraints, occupancy tracking, housing systems, and status.
          </p>
        </div>
        <button onClick={() => setShowAddForm(true)} className="btn-primary flex-shrink-0">
          <Plus size={15} />
          Add House
        </button>
      </div>

      {/* Summary Cards */}
      <HousesSummaryCards />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, code, farm, flock batch…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field pl-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-gray-400" />
          <select className="select-field w-auto text-sm" value={farmFilter} onChange={e => setFarmFilter(e.target.value)}>
            <option value="all">All Farms</option>
            {uniqueFarms.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select className="select-field w-auto text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="available">Available</option>
            <option value="occupied">Occupied</option>
            <option value="cleaning">Cleaning</option>
            <option value="disinfection">Disinfection</option>
            <option value="maintenance">Maintenance</option>
            <option value="inactive">Inactive</option>
          </select>
          <select className="select-field w-auto text-sm" value={systemFilter} onChange={e => setSystemFilter(e.target.value)}>
            <option value="all">All Systems</option>
            <option value="deep_litter">Deep Litter</option>
            <option value="battery_cage">Battery Cage</option>
            <option value="free_range">Free Range</option>
            <option value="brooder">Brooder</option>
            <option value="breeder_house">Breeder House</option>
          </select>
        </div>
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} house{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Houses Table */}
      <div className="panel-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">House</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Farm / Branch</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">System</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-48">Occupancy</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Flock</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-400">
                    <Home size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm font-medium">No houses match your filters.</p>
                  </td>
                </tr>
              )}
              {filtered.map(house => {
                const isExpanded = expandedId === house.id;
                return (
                  <React.Fragment key={house.id}>
                    <tr
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : house.id)}
                    >
                      {/* House */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={e => { e.stopPropagation(); setExpandedId(isExpanded ? null : house.id); }}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </button>
                          <div>
                            <p className="font-semibold text-gray-900">{house.houseName}</p>
                            <p className="text-xs text-gray-400 font-mono">{house.houseCode}</p>
                          </div>
                        </div>
                      </td>
                      {/* Farm */}
                      <td className="px-4 py-3">
                        <p className="text-gray-800 font-medium">{house.farmName}</p>
                        {house.branchName && <p className="text-xs text-gray-400">{house.branchName}</p>}
                      </td>
                      {/* System */}
                      <td className="px-4 py-3">
                        <SystemBadge system={house.housingSystem} />
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge status={house.status} />
                      </td>
                      {/* Occupancy */}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <OccupancyBar current={house.currentOccupancy} capacity={house.capacity} />
                          <p className="text-xs text-gray-400 font-tabular">
                            {house.currentOccupancy.toLocaleString()} / {house.capacity.toLocaleString()}
                          </p>
                        </div>
                      </td>
                      {/* Flock */}
                      <td className="px-4 py-3">
                        {house.flockBatch ? (
                          <div>
                            <p className="text-xs font-semibold text-blue-700">{house.flockBatch}</p>
                            <p className="text-xs text-gray-400">{house.flockType}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setDetailHouse(house)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                            title="View details"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => setEditHouse(house)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                            title="Edit house"
                          >
                            <Edit2 size={14} />
                          </button>
                          <div className="relative">
                            <button
                              onClick={() => setOpenMenuId(openMenuId === house.id ? null : house.id)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                              title="Change status"
                            >
                              <MoreVertical size={14} />
                            </button>
                            {openMenuId === house.id && (
                              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-xl shadow-xl z-20 py-1">
                                <p className="text-xs font-semibold text-gray-400 px-3 py-1.5 uppercase tracking-wide">Set Status</p>
                                {(['available', 'occupied', 'cleaning', 'disinfection', 'maintenance', 'inactive'] as HouseStatus[]).map(s => (
                                  <button
                                    key={s}
                                    onClick={() => handleStatusChange(house.id, s)}
                                    className={`w-full text-left px-3 py-1.5 text-xs font-medium hover:bg-gray-50 transition-colors flex items-center gap-2 ${house.status === s ? 'text-green-700 bg-green-50' : 'text-gray-700'}`}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full ${HOUSE_STATUS_COLORS[s].split(' ')[0].replace('bg-', 'bg-')}`} />
                                    {HOUSE_STATUS_LABELS[s]}
                                    {house.status === s && <span className="ml-auto text-green-600">✓</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Row */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Dimensions</p>
                              <p className="font-medium text-gray-800">{house.length}m × {house.width}m</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Ventilation</p>
                              <p className="font-medium text-gray-800">{house.ventilationType || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Water System</p>
                              <p className="font-medium text-gray-800">{house.waterSystem || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Feeding System</p>
                              <p className="font-medium text-gray-800">{house.feedingSystem || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Last Cleaned</p>
                              <p className={`font-medium ${!house.lastCleanedDate ? 'text-red-500' : 'text-gray-800'}`}>{house.lastCleanedDate || 'Not recorded'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Last Disinfected</p>
                              <p className={`font-medium ${!house.lastDisinfectedDate ? 'text-red-500' : 'text-gray-800'}`}>{house.lastDisinfectedDate || 'Not recorded'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Next Maintenance</p>
                              <p className="font-medium text-gray-800">{house.nextMaintenanceDate || 'Not scheduled'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Manager</p>
                              <p className="font-medium text-gray-800">{house.manager || '—'}</p>
                            </div>
                            {house.notes && (
                              <div className="col-span-2 md:col-span-4">
                                <p className="text-xs text-gray-400 mb-0.5">Notes</p>
                                <p className="text-gray-700">{house.notes}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Click outside to close menu */}
      {openMenuId && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
      )}
    </div>
  );
}
